import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: 'intro',
      title: 'Introduction',
      content: (
        <>
          <p>This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use Lumisoro, and explains your privacy rights and how the law protects you.</p>
          <p>We use your personal data to provide and improve the Service. By using Lumisoro, you agree to the collection and use of information in accordance with this Privacy Policy.</p>
        </>
      )
    },
    {
      id: 'definitions',
      title: 'Definitions',
      content: (
        <ul>
          <li><strong>Account:</strong> A unique account created for you to access our Service</li>
          <li><strong>Application:</strong> The software program provided by the Company, downloaded by you on any electronic device, named Lumisoro</li>
          <li><strong>Company:</strong> Refers to Lumisoro (also "We", "Us", or "Our")</li>
          <li><strong>Device:</strong> Any device that can access the Service such as a smart TV, smartphone, or tablet</li>
          <li><strong>Personal Data:</strong> Any information that relates to an identified or identifiable individual</li>
          <li><strong>Service:</strong> Refers to the Lumisoro Application</li>
          <li><strong>Usage Data:</strong> Data collected automatically from use of the Service</li>
        </ul>
      )
    },
    {
      id: 'data-collection',
      title: 'Data We Collect',
      content: (
        <>
          <h4>Personal Data</h4>
          <p>While using Lumisoro, we may ask you to provide personally identifiable information that can be used to contact or identify you, including:</p>
          <ul>
            <li>Email address</li>
            <li>First name and last name</li>
            <li>Phone number</li>
            <li>Address, State, Province, ZIP/Postal code, City</li>
          </ul>
          <h4>Usage Data</h4>
          <p>We automatically collect information about how you use the Service, including:</p>
          <ul>
            <li>Device IP address and type</li>
            <li>Browser type and version</li>
            <li>Pages visited and time spent</li>
            <li>Unique device identifiers</li>
            <li>Operating system and mobile network information</li>
          </ul>
        </>
      )
    },
    {
      id: 'data-usage',
      title: 'How We Use Your Data',
      content: (
        <>
          <p>Lumisoro uses your Personal Data for these purposes:</p>
          <ul>
            <li><strong>Provide and maintain the Service</strong> - Including monitoring usage and performance</li>
            <li><strong>Manage your account</strong> - Registration and access to Service features</li>
            <li><strong>Contact you</strong> - Via email, phone, or in-app notifications about updates</li>
            <li><strong>Provide support</strong> - Manage your requests and inquiries</li>
            <li><strong>Improve our Service</strong> - Data analysis and user experience evaluation</li>
            <li><strong>Business transfers</strong> - Mergers, acquisitions, or restructuring</li>
          </ul>
          <p>We may share your information with:</p>
          <ul>
            <li>Service Providers to help deliver or analyze the Service</li>
            <li>Affiliates and business partners for legitimate operational reasons</li>
            <li>With your consent, for any additional purposes</li>
          </ul>
        </>
      )
    },
    {
      id: 'data-retention',
      title: 'Data Retention & Transfer',
      content: (
        <>
          <p>We retain Personal Data only as long as necessary for the purposes described in this policy or as required by law. Usage Data is generally retained for a shorter period unless required for security or legal compliance.</p>
          <p>Your information may be processed and stored on computers outside your region or country. By using Lumisoro, you consent to such transfers in accordance with this Privacy Policy.</p>
        </>
      )
    },
    {
      id: 'disclosure',
      title: 'Data Disclosure',
      content: (
        <>
          <p>We may disclose your Personal Data to:</p>
          <ul>
            <li>Comply with legal obligations</li>
            <li>Protect and defend the rights or property of Lumisoro</li>
            <li>Prevent or investigate possible wrongdoing</li>
            <li>Protect users and the public from harm</li>
            <li>Complete business transfers</li>
          </ul>
        </>
      )
    },
    {
      id: 'security',
      title: 'Security',
      content: (
        <p>We use commercially reasonable methods to protect your Personal Data. However, no transmission over the Internet or electronic storage method is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
      )
    },
    {
      id: 'children',
      title: 'Children\'s Privacy',
      content: (
        <p>Lumisoro is not directed to individuals under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us information, please contact us so we can remove it.</p>
      )
    },
    {
      id: 'links',
      title: 'Third-Party Links',
      content: (
        <p>Lumisoro may contain links to external sites not operated by us. We are not responsible for the content or privacy practices of third-party websites and recommend reviewing their policies.</p>
      )
    },
    {
      id: 'changes',
      title: 'Policy Updates',
      content: (
        <p>We may update this Privacy Policy from time to time. Changes will be posted here with a revised "Last updated" date. We will notify you of significant updates via email or in-app notice where appropriate.</p>
      )
    }
  ];

  return (
    <div className="privacy-container-modern">
      <div className="privacy-header">
        <Link to="/" className="back-link-modern">← Back to Home</Link>
        <h1 className="privacy-title-modern">Privacy Policy</h1>
        <p className="privacy-date">Last updated: February 09, 2022</p>
      </div>

      {/* Quick Summary */}
      <div className="privacy-summary">
        <h2 className="summary-title">Quick Summary</h2>
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">🔒</div>
            <h3>Your Data is Protected</h3>
            <p>We use industry-standard security measures to protect your information</p>
          </div>
          <div className="summary-card">
            <div className="summary-icon">👤</div>
            <h3>No Data Selling</h3>
            <p>We never sell your personal data to third parties</p>
          </div>
          <div className="summary-card">
            <div className="summary-icon">✅</div>
            <h3>You're in Control</h3>
            <p>Access, correct, or delete your data at any time</p>
          </div>
        </div>
      </div>

      {/* Accordion Sections */}
      <div className="privacy-accordion">
        {sections.map((section) => (
          <div key={section.id} className={`accordion-item ${openSections[section.id] ? 'open' : ''}`}>
            <button
              className="accordion-header"
              onClick={() => toggleSection(section.id)}
            >
              <span className="accordion-title">{section.title}</span>
              <span className="accordion-icon">{openSections[section.id] ? '−' : '+'}</span>
            </button>
            {openSections[section.id] && (
              <div className="accordion-content">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact Section */}
      <div className="privacy-contact">
        <h2 className="contact-title-privacy">Questions About Privacy?</h2>
        <p className="contact-text-privacy">
          If you have any questions about this Privacy Policy, please contact us:
        </p>
        <a href="mailto:Support@lumisoro.com" className="contact-email-link">
          📧 Support@lumisoro.com
        </a>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
